import DownArrowMinimal from "@/svg/DownArrowMinimal";
import { useState } from "react";
import { useEligibilityDetailContext } from "../EligibilityDetailContext";
import DiagnosisSummaryRow from "./DiagnosisSummaryRow";

/**
 * Rounded class for one stacked additional-diagnosis card.
 *
 * @param index - Card index
 * @param total - Total additional cards
 * @returns Tailwind rounding classes
 */
const getSubContentCardRoundedClass = (index: number, total: number) => {
  if (total === 1) {
    return "rounded-lg";
  }
  if (index === 0) {
    return "rounded-lg rounded-bl-none rounded-br-none";
  }
  if (index === total - 1) {
    return "rounded-lg rounded-tl-none rounded-tr-none";
  }
  return "rounded-none";
};

/**
 * Border class for one stacked additional-diagnosis card.
 *
 * @param index - Card index
 * @param total - Total additional cards
 * @returns Tailwind border classes
 */
const getSubContentCardBorderClass = (index: number, total: number) => {
  if (total <= 1) {
    return "";
  }
  if (index === 0) {
    return "border-b-0";
  }
  if (index === total - 1) {
    return "border-t-0";
  }
  return "";
};

/**
 * Diagnosis summary card populated from the eligibility detail patient strip.
 *
 * @returns Diagnosis card
 */
const DiagnosisCard = () => {
  const { header } = useEligibilityDetailContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const additional = header.diagnosis.additional;

  return (
    <div className="single-diagnosis-card bg-white  border border-primaryGray-14 rounded-lg">
      <div className="main-content flex w-full gap-4 p-4">
        <div className="left-content flex-1">
          <DiagnosisSummaryRow data={header.diagnosis.primary} />
        </div>
        {additional.length > 0 && (
          <div className="right-content flex items-center justify-center">
            <div
              className={`icon hover:cursor-pointer hover:scale-105 transition-all duration-300 ${isExpanded ? "rotate-180" : ""}`}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <DownArrowMinimal />
            </div>
          </div>
        )}
      </div>
      {additional.length > 0 && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <div className="sub-content border-t border-primaryGray-14 p-4 max-h-60 overflow-y-auto">
              {additional.map((diagnosis, index) => (
                <div
                  key={`${diagnosis.name}-${index}`}
                  className={`subcontent-card border p-4 border-primaryGray-14 ${getSubContentCardRoundedClass(index, additional.length)} ${getSubContentCardBorderClass(index, additional.length)}`}
                >
                  <DiagnosisSummaryRow data={diagnosis} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosisCard;
